import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalizedUrl } from "@/hooks/use-localized-routes";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUiTranslations } from "@/hooks/use-ui-translations";

interface SearchResult {
  id: string;
  title: string;
  slug: string;
  category?: string;
  location?: string;
  images?: string[];
}

export function AiSearchField() {
  const { t } = useUiTranslations();
  const navigate = useNavigate();
  const { localizeUrl } = useLocalizedUrl();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("ai-experience-search", {
          body: { query: q },
        });
        if (error) throw error;
        setResults(data?.results || []);
        setOpen(true);
      } catch (err) {
        console.error("AI search error:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    search(e.target.value);
  };

  const handleSelect = (slug: string) => {
    setOpen(false);
    setQuery("");
    navigate(localizeUrl(`/experiences/${slug}`));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (results.length > 0) {
      handleSelect(results[0].slug);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-xl mx-auto">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/60" />
          <input
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={() => results.length > 0 && setOpen(true)}
            placeholder={t("search.placeholder")}
            className="w-full rounded-full border border-white/20 bg-white/10 py-3.5 pl-12 pr-12 text-sm text-white placeholder:text-white/50 backdrop-blur-md focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
          />
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin text-white/60" />
            ) : (
              <Sparkles className="h-4 w-4 text-white/40" />
            )}
          </div>
        </div>
      </form>

      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          <div className="p-1.5">
            {results.slice(0, 6).map((r) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r.slug)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent"
              >
                {r.images?.[0] ? (
                  <img
                    src={r.images[0]}
                    alt=""
                    className="h-10 w-10 rounded-md object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-muted flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{r.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[r.category, r.location].filter(Boolean).join(" · ")}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
